import React from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { ClockIcon, BrainIcon, TargetIcon, SparklesIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
export function AppComingSoonPage() {
  return <div className="min-h-screen bg-gradient-to-b from-sky-50 via-lavender-50 to-blush-50 pt-24 pb-24 overflow-hidden" data-id="element-144">
      <SparkleEffect count={120} data-id="element-145" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-146">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pt-12" data-id="element-147">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-148">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-sky-200 text-sm font-bold text-sky-700 mb-8 shadow-sm uppercase tracking-wider" data-id="element-149">
              <SparklesIcon className="w-4 h-4 animate-pulse" data-id="element-150" />
              Coming Soon
            </div>

            <h1 className="font-heading text-5xl md:text-7xl font-bold text-plum-700 mb-6 leading-tight" data-id="element-151">
              Something <span className="shimmer-text" data-id="element-152">Big</span> Is Brewing.
            </h1>

            <p className="text-xl text-plum-500 mb-8 leading-relaxed font-medium" data-id="element-153">
              Meet your new AI-powered life scheduler. It doesn't just manage
              your time—it designs your day down to the minute, seamlessly
              integrating your healing routines, goals, and the rest you
              actually need.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-154">
              {[{
              icon: <ClockIcon className="w-5 h-5 text-cta-500" data-id="element-155" />,
              text: 'Minute-by-minute intelligent scheduling'
            }, {
              icon: <BrainIcon className="w-5 h-5 text-cta-500" data-id="element-156" />,
              text: 'AI that learns your energy patterns'
            }, {
              icon: <TargetIcon className="w-5 h-5 text-cta-500" data-id="element-157" />,
              text: 'Built-in habit and healing tracking'
            }].map((feature, i) => <li key={i} className="flex items-center gap-3 text-plum-700 font-bold" data-id="element-158">
                  <div className="bg-white p-2 rounded-full shadow-sm" data-id="element-159">
                    {feature.icon}
                  </div>
                  {feature.text}
                </li>)}
            </ul>

            <div className="bg-white/60 backdrop-blur-md p-6 rounded-3xl border border-white shadow-lg" data-id="element-160">
              <h3 className="font-bold text-plum-700 mb-2" data-id="element-161">Get early access</h3>
              <form className="flex gap-2" onSubmit={e => e.preventDefault()} data-id="element-162">
                <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none" data-id="element-163" />
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-plum-700 text-white rounded-xl font-bold hover:bg-plum-900 transition-colors" data-id="element-164">
                  <GlitterHeart size={16} variant="light" data-id="element-165" />
                  Join Waitlist
                </button>
              </form>
            </div>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative mx-auto w-full max-w-[320px]" data-id="element-166">
            {/* Decorative glow behind phone */}
            <div className="absolute inset-0 bg-gradient-to-tr from-cta-500/20 to-sky-400/20 blur-3xl rounded-full" data-id="element-167" />

            <div className="relative h-[650px] bg-white rounded-[3rem] border-[12px] border-white shadow-2xl overflow-hidden flex flex-col" data-id="element-168">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-7 bg-white rounded-b-2xl w-40 mx-auto z-20" data-id="element-169" />

              {/* Screen Content */}
              <div className="flex-1 bg-blush-50 p-6 pt-14 overflow-hidden relative" data-id="element-170">
                <div className="flex justify-between items-center mb-8" data-id="element-171">
                  <div data-id="element-172">
                    <h3 className="font-heading text-2xl font-bold text-plum-700" data-id="element-173">
                      Good Morning
                    </h3>
                    <p className="text-sm text-plum-500 font-medium" data-id="element-174">
                      Your aligned day awaits.
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-175">
                    <SparklesIcon className="w-5 h-5 text-cta-500" data-id="element-176" />
                  </div>
                </div>

                {/* Fake Schedule Items */}
                <div className="space-y-4" data-id="element-177">
                  <div className="text-xs font-bold text-plum-400 uppercase tracking-wider mb-2" data-id="element-178">
                    Up Next
                  </div>

                  <motion.div animate={{
                  y: [0, -5, 0]
                }} transition={{
                  repeat: Infinity,
                  duration: 4
                }} className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-cta-500" data-id="element-179">
                    <div className="flex justify-between items-start mb-2" data-id="element-180">
                      <h4 className="font-bold text-plum-700" data-id="element-181">
                        Morning Meditation
                      </h4>
                      <span className="text-xs font-bold text-cta-500 bg-blush-100 px-2 py-1 rounded-md" data-id="element-182">
                        8:00 AM
                      </span>
                    </div>
                    <p className="text-xs text-plum-500 font-medium" data-id="element-183">
                      15 mins • Nervous system regulation
                    </p>
                  </motion.div>

                  <div className="bg-white/60 p-4 rounded-2xl border border-white" data-id="element-184">
                    <div className="flex justify-between items-start mb-2" data-id="element-185">
                      <h4 className="font-bold text-plum-700" data-id="element-186">
                        Deep Work Block
                      </h4>
                      <span className="text-xs font-bold text-plum-500" data-id="element-187">
                        8:30 AM
                      </span>
                    </div>
                    <p className="text-xs text-plum-500 font-medium" data-id="element-188">
                      90 mins • Project planning
                    </p>
                  </div>

                  <div className="bg-lavender-100/50 p-4 rounded-2xl border border-lavender-200" data-id="element-189">
                    <div className="flex justify-between items-start mb-2" data-id="element-190">
                      <h4 className="font-bold text-plum-700" data-id="element-191">
                        Somatic Check-in
                      </h4>
                      <span className="text-xs font-bold text-plum-500" data-id="element-192">
                        10:00 AM
                      </span>
                    </div>
                    <p className="text-xs text-plum-500 font-medium" data-id="element-193">
                      5 mins • Stretch & breathe
                    </p>
                  </div>
                </div>

                {/* Bottom Nav Mockup */}
                <div className="absolute bottom-0 inset-x-0 h-20 bg-white/80 backdrop-blur-md border-t border-white flex justify-around items-center px-6 pb-4" data-id="element-194">
                  <div className="w-6 h-6 rounded-full bg-plum-700" data-id="element-195" />
                  <div className="w-6 h-6 rounded-full bg-plum-200" data-id="element-196" />
                  <div className="w-12 h-12 rounded-full bg-cta-500 -mt-8 flex items-center justify-center shadow-lg text-white" data-id="element-197">
                    <SparklesIcon className="w-6 h-6" data-id="element-198" />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-plum-200" data-id="element-199" />
                  <div className="w-6 h-6 rounded-full bg-plum-200" data-id="element-200" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}